import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Table, Button, Form, Spinner, Alert, Container, Row, Col, Modal, Badge } from 'react-bootstrap';
import { useLocation, Link, useParams } from 'react-router-dom';
import { IconButton, Tooltip } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../services/api';
import './ChangeLogManager.css';

// --- Constants ---
const CHANGE_REQUEST_TYPES = [
  { value: 'IN_TEST_LINK', label: 'In Test Link' },
  { value: 'LIVE', label: 'Live' },
  { value: 'POST_LAUNCH', label: 'Post-Launch' },
];

const TYPE_OF_CHANGE_OPTIONS = [
  { value: 'TEXT_CHANGE_PRIMARY', label: 'Question Text Change - Primary' },
  { value: 'TEXT_CHANGE_SECONDARY', label: 'Question Text Change - Secondary' },
  { value: 'QUESTION_TYPE_CHANGE', label: 'Question Type Change' },
  { value: 'ROUTING_ISSUE', label: 'Routing Issue' },
  { value: 'LOGIC_CHANGE', label: 'Logic Change' },
  { value: 'PIPING_ISSUE', label: 'Piping Issue' },
  { value: 'NEW_QUESTION', label: 'New Question' },
  { value: 'NEW_OPTIONS', label: 'Change in option list' },
  { value: 'LOOP_CHANGE', label: 'Changes in Loop' },
  { value: 'LOGIC_ISSUE', label: 'Logic Issue' },
  { value: 'ALIGNMENT_ISSUE', label: 'Alignment Issue' },
  { value: 'OPTION_BRAND_MISSING', label: 'Option/Brand Missing' },
  { value: 'CRITICAL_ISSUE', label: 'Critical Issue' },
  { value: 'WRONG_QUESTION_LABEL', label: 'Wrong Question Labelling' },
  { value: 'QUESTION_MISSING', label: 'Question Missing' },
  { value: 'RAISE_CLARIFICATION', label: 'Raise Clarification' },
  { value: 'OTHER', label: 'Other' },
];

const FINAL_STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_PROGRESS', label: 'In-Progress' },
  { value: 'NOT_IMPLEMENTED', label: 'Not-Implemented' },
  { value: 'IMPLEMENTED', label: 'Implemented' },
  { value: 'CONFIRMED', label: 'Verified' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'REPEATED_ISSUE', label: 'Repeated Issue' },
];

const highlightQuoted = (text) => {
  if (!text) return null;
  const parts = [];
  const regex = /(['"])(.*?)\1/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <span key={key++} style={{ fontWeight: 700, color: '#00c3ff' }}>{match[0]}</span>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? parts : text;
};

const getTodaysDate = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

const INITIAL_FORM_STATE = {
  change_request: 'IN_TEST_LINK',
  question_number: '',
  type_of_change: 'OTHER',
  ai_category: '',
  change_details: '',
  date_of_request: getTodaysDate(),
  scripting_resource_feedback: '',
  cs_feedback: '',
  final_status: 'OPEN',
};

function ChangeLogManager() {

  const [changeLogs, setChangeLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newEntry, setNewEntry] = useState(INITIAL_FORM_STATE);
  const [isSaving, setIsSaving] = useState(false);
  const [editRowId, setEditRowId] = useState(null);
  const [editData, setEditData] = useState(INITIAL_FORM_STATE);
  const [isPosting, setIsPosting] = useState(false);
  const postingInFlight = useRef(false);
  const changedPinnedFiles = useRef({ questionnaire: null, analysis_plan: null });
  const [postMessage, setPostMessage] = useState({ type: '', text: '' });
  const [triggerEmail, setTriggerEmail] = useState(false);

  const [filters, setFilters] = useState({ status: '', change_request: '', type_of_change: '', requested_by: '', search: '' });

  const [newEntryError, setNewEntryError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [filesFrozen, setFilesFrozen] = useState(false);
  const [receiverNote, setReceiverNote] = useState("");

  // Pinned attachments (QNR / Analysis Plan)
  const [pinnedAttachments, setPinnedAttachments] = useState({ questionnaire: null, analysis_plan: null });
  const [attachUploading, setAttachUploading] = useState({ questionnaire: false, analysis_plan: false });
  const attachFileRefs = useRef({});

  // Pinned links
  const [pinnedLinks, setPinnedLinks] = useState({ test: [], live: [], quota: [] });

  // AI Feedback Extraction
  const [fetchDialog, setFetchDialog] = useState(false);
  const [fetchedItems, setFetchedItems] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');

  // AI Classify Other
  const [classifyDialog, setClassifyDialog] = useState(false);
  const [classifyItems, setClassifyItems] = useState([]);
  const [classifyLoading, setClassifyLoading] = useState(false);
  const [classifyError, setClassifyError] = useState('');
  const [classifyApplying, setClassifyApplying] = useState(false);
  const [aiCategorizing, setAiCategorizing] = useState(false);
  const [aiClassifiedTypes, setAiClassifiedTypes] = useState(new Set());
  const [categoryEditId, setCategoryEditId] = useState(null);
  const [showQnrPanel, setShowQnrPanel] = useState(false);
  const [qnrInstructions, setQnrInstructions] = useState(null);
  const [qnrGenerating, setQnrGenerating] = useState(false);
  const [qnrUpdating, setQnrUpdating] = useState(false);
  const [qnrError, setQnrError] = useState('');
  const qnrFileInputRef = useRef(null);

  const [isDP, setIsDP] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [headerCollapsed, setHeaderCollapsed] = useState(() => {
    try { return localStorage.getItem('cl_header_collapsed') === '1'; } catch { return false; }
  });

  const location = useLocation();
  const { segmentId: segmentIdFromParams } = useParams();
  const { project } = location.state || {};
  const segmentId = segmentIdFromParams || project?.segment_id;
  const [segmentName, setSegmentName] = useState(project?.segment_name || '');

  const processedLogs = useMemo(() => {
    if (!changeLogs.length) return [];
    return changeLogs.map((log, index) => ({ ...log, displayIndex: index + 1 }));
  }, [changeLogs]);

  const uniqueRequesters = useMemo(() => {
    const names = processedLogs.map(l => l.created_by?.username).filter(Boolean);
    return [...new Set(names)].sort();
  }, [processedLogs]);

  const typeCounts = useMemo(() => {
    const { status, change_request, requested_by, search } = filters;
    const q = search.toLowerCase();
    const counts = {};
    processedLogs.forEach(log => {
      if (status && log.final_status !== status) return;
      if (change_request && log.change_request !== change_request) return;
      if (requested_by && log.created_by?.username !== requested_by) return;
      if (q && !`${log.question_number} ${log.change_details} ${log.cs_feedback} ${log.scripting_resource_feedback}`.toLowerCase().includes(q)) return;
      if (log.type_of_change) counts[log.type_of_change] = (counts[log.type_of_change] || 0) + 1;
    });
    return counts;
  }, [processedLogs, filters]);

  const filteredLogs = useMemo(() => {
    const { status, change_request, type_of_change, requested_by, search } = filters;
    const q = search.toLowerCase();
    return processedLogs.filter(log => {
      if (status && log.final_status !== status) return false;
      if (change_request && log.change_request !== change_request) return false;
      if (type_of_change && log.type_of_change !== type_of_change) return false;
      if (requested_by && log.created_by?.username !== requested_by) return false;
      if (q && !`${log.question_number} ${log.change_details} ${log.cs_feedback} ${log.scripting_resource_feedback}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [processedLogs, filters]);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const changesByQuestion = useMemo(() => {
    const grouped = {};
    processedLogs.forEach(log => {
      const key = log.question_number?.trim() || '—';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(log);
    });
    // sort each group: ISSUE first, then CHANGE, then NEW_ADDITION, then unset
    const catOrder = { ISSUE: 0, CHANGE: 1, NEW_ADDITION: 2 };
    Object.values(grouped).forEach(arr => {
      arr.sort((a, b) => (catOrder[a.ai_category] ?? 3) - (catOrder[b.ai_category] ?? 3));
    });
    // sort questions: numbered ones first (natural sort), then '—'
    return Object.entries(grouped).sort(([a], [b]) => {
      if (a === '—') return 1;
      if (b === '—') return -1;
      return a.localeCompare(b, undefined, { numeric: true });
    });
  }, [processedLogs]);

  useEffect(() => {
    if (!segmentId) {
      setIsLoading(false);
      setError("No Segment ID found. Please select a project.");
      return;
    }

    const fetchData = async () => {
      try {
        if (!segmentName) {
          try {
            const nameRes = await api.get(`/api/segments/get-segment-name-by-id/?id=${segmentId}`);
            setSegmentName(nameRes.data.segment_name);
          } catch {
            setSegmentName(`Segment ${segmentId}`);
          }
        }

        const logsRes = await api.get(`/logs/changelogs/?segment_id=${segmentId}`);
        setChangeLogs(logsRes.data);

        try {
          const deptRes = await api.get('/users/my-department/', { headers: { Authorization: `Token ${localStorage.getItem('Token')}` } });
          setIsDP(deptRes.data?.is_dp_or_scripting || false);
        } catch { }

        try {
          const attRes = await api.get(`/api/pinned-attachments/?segment_id=${segmentId}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
          setPinnedAttachments({ questionnaire: attRes.data.questionnaire || null, analysis_plan: attRes.data.analysis_plan || null });
        } catch { }

        try {
          const linksRes = await api.get(`api/pinned-links/?segment_id=${segmentId}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
          setPinnedLinks({ test: linksRes.data.test || [], live: linksRes.data.live || [], quota: linksRes.data.quota || [] });
        } catch { }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [segmentId]);

  const handleAttachmentUpload = async (attachType, file) => {
    setAttachUploading(prev => ({ ...prev, [attachType]: true }));
    try {
      const formData = new FormData();
      formData.append('segment_id', segmentId);
      formData.append('attachment_type', attachType);
      formData.append('file', file);
      const res = await api.post('/api/pinned-attachments/upload/', formData, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      setPinnedAttachments(prev => ({ ...prev, [attachType]: res.data }));
      changedPinnedFiles.current[attachType] = file;
    } catch { } finally {
      setAttachUploading(prev => ({ ...prev, [attachType]: false }));
    }
  };

  const handleAttachmentDownload = async (fileUrl, filename) => {
    try {
      const res = await fetch(fileUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleAttachmentDelete = async (attachType) => {
    if (!window.confirm('Remove this attachment?')) return;
    try {
      await api.delete(`/api/pinned-attachments/delete/?segment_id=${segmentId}&attachment_type=${attachType}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      setPinnedAttachments(prev => ({ ...prev, [attachType]: null }));
    } catch { }
  };

  const handleNewChange = (e) => {
    const { name, value } = e.target;
    setNewEntry((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveNew = async () => {
    if (!segmentId || !newEntry.question_number || !newEntry.change_details || !newEntry.date_of_request) {
      setNewEntryError('Please fill required fields: Question and Details are mandatory.');
      return;
    }
    setNewEntryError('');

    try {
      setIsSaving(true);
      const payload = { ...newEntry, segment: segmentId, created_by_name: localStorage.getItem("username") };
      const created = await api.post('/logs/changelogs/', payload);
      setNewEntry(INITIAL_FORM_STATE);
      const response = await api.get(`/logs/changelogs/?segment_id=${segmentId}`);
      setChangeLogs(response.data);
    } catch (err) {
      setError(err?.response?.data ? JSON.stringify(err.response.data) : 'Failed to save entry. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditClick = (log) => { setEditRowId(log.id); setEditData({ ...log }); };
  const handleEditChange = (e) => { setEditData({ ...editData, [e.target.name]: e.target.value }); };

  const handleSaveEdit = async (id) => {
    try {
      setIsSaving(true);
      await api.patch(`/logs/changelogs/${id}/`, editData);
      setEditRowId(null);
      const response = await api.get(`/logs/changelogs/?segment_id=${segmentId}`);
      setChangeLogs(response.data);
    } catch (err) {
      setError(err?.response?.data ? JSON.stringify(err.response.data) : 'Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => { setEditRowId(null); setEditData(INITIAL_FORM_STATE); };

  const handleToggleResearcher = async (logId, currentValue) => {
    setTogglingId(logId);
    try {
      await api.patch(`/logs/changelogs/${logId}/`, { visible_to_researcher: !currentValue }, {
        headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
      });
      setChangeLogs(prev => prev.map(l => l.id === logId ? { ...l, visible_to_researcher: !currentValue } : l));
    } catch {
      // silently ignore — UI stays in previous state
    } finally {
      setTogglingId(null);
    }
  };

  const handleFetchFeedbacks = async () => {
    setFetchLoading(true);
    setFetchError('');
    setFetchedItems([]);
    setFetchDialog(true);
    try {
      const res = await api.post('/logs/changelogs/extract-feedbacks/', { segment_id: segmentId });
      const items = (res.data.items || []).map((item, i) => ({
        ...item,
        _id: i,
        _selected: true,
        date_of_request: getTodaysDate(),
      }));
      setFetchedItems(items);
    } catch (err) {
      setFetchError(err?.response?.data?.error || 'Failed to extract feedbacks from discussion.');
    } finally {
      setFetchLoading(false);
    }
  };

  const updateFetchedItem = (id, field, value) => {
    setFetchedItems(prev => prev.map(item => item._id === id ? { ...item, [field]: value } : item));
  };

  const handleSaveExtracted = async () => {
    const toSave = fetchedItems.filter(i => i._selected);
    if (!toSave.length) { setFetchDialog(false); return; }
    try {
      setFetchLoading(true);
      for (const item of toSave) {
        const created = await api.post('/logs/changelogs/', {
          segment: segmentId,
          question_number: item.question_number || '',
          type_of_change: item.type_of_change || 'OTHER',
          change_details: item.change_details || '',
          change_request: item.change_request || 'IN_TEST_LINK',
          final_status: 'OPEN',
          date_of_request: item.date_of_request,
          source: 'discussion',
        });
      }
      const response = await api.get(`/logs/changelogs/?segment_id=${segmentId}`);
      setChangeLogs(response.data);
      setFetchDialog(false);
      setFetchedItems([]);
    } catch {
      setFetchError('Failed to save some entries. Please try again.');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleInlineCategoryChange = async (logId, newCategory) => {
    setCategoryEditId(null);
    try {
      await api.patch(`/logs/changelogs/${logId}/`, { ai_category: newCategory });
      setChangeLogs(prev => prev.map(l => l.id === logId ? { ...l, ai_category: newCategory } : l));
    } catch (e) {
      console.error('Category update failed', e);
    }
  };

  const handleAiCategorize = async () => {
    setAiCategorizing(true);
    try {
      const res = await api.post('/logs/changelogs/ai-categorize/', { segment_id: segmentId });
      const updates = res.data.entries || [];
      setChangeLogs(prev => prev.map(log => {
        const match = updates.find(u => u.id === log.id);
        return match ? { ...log, ai_category: match.ai_category } : log;
      }));
    } catch (e) {
      console.error('AI categorize failed', e);
    } finally {
      setAiCategorizing(false);
    }
  };

  const handleGenerateQnrInstructions = async () => {
    if (!segmentId) return;
    setQnrGenerating(true);
    setQnrError('');
    setQnrInstructions(null);
    try {
      const res = await api.post('/logs/changelogs/qnr-instructions/', { segment_id: segmentId });
      setQnrInstructions(res.data);
    } catch (e) {
      setQnrError(e.response?.data?.error || 'AI analysis failed. Please try again.');
    } finally {
      setQnrGenerating(false);
    }
  };

  const handleUpdateQnr = async () => {
    if (!segmentId) return;
    setQnrUpdating(true);
    setQnrError('');
    try {
      const res = await api.post('/logs/changelogs/update-qnr/', { segment_id: segmentId }, { responseType: 'blob' });
      const applied = res.headers['x-changes-applied'] || '?';
      const skipped = res.headers['x-changes-skipped'] || '0';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : 'QNR_AI_Updated.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      if (skipped > 0) setQnrError(`Downloaded! ${applied} changes applied, ${skipped} skipped (routing/logic — check AI_Changes_Log sheet).`);
    } catch (e) {
      let msg = 'Failed to generate updated QNR.';
      if (e.response?.data) {
        try { const t = await e.response.data.text(); msg = JSON.parse(t).error || msg; } catch {}
      }
      setQnrError(msg);
    } finally {
      setQnrUpdating(false);
    }
  };

  const handleClassifyOther = async () => {
    setClassifyLoading(true);
    setClassifyError('');
    setClassifyItems([]);
    setClassifyDialog(true);
    try {
      const res = await api.post('/logs/changelogs/classify-other/', { segment_id: segmentId });
      const suggestions = (res.data.suggestions || []).map(s => ({ ...s, _accepted: s.suggested_type !== 'OTHER', _type: s.suggested_type }));
      setClassifyItems(suggestions);
      if (!suggestions.length) setClassifyError(res.data.message || 'No OTHER entries found.');
    } catch (err) {
      setClassifyError(err?.response?.data?.error || 'Classification failed. Please try again.');
    } finally {
      setClassifyLoading(false);
    }
  };

  const handleApplyClassifications = async () => {
    const toApply = classifyItems.filter(i => i._accepted && i._type !== 'OTHER');
    if (!toApply.length) { setClassifyDialog(false); return; }
    setClassifyApplying(true);
    try {
      await api.post('/logs/changelogs/apply-classifications/', {
        updates: toApply.map(i => ({ id: i.id, type_of_change: i._type })),
      });
      const appliedTypes = new Set(toApply.map(i => i._type));
      const response = await api.get(`/logs/changelogs/?segment_id=${segmentId}`);
      setChangeLogs(response.data);
      setAiClassifiedTypes(appliedTypes);
      setClassifyDialog(false);
      setClassifyItems([]);
    } catch {
      setClassifyError('Failed to apply classifications. Please try again.');
    } finally {
      setClassifyApplying(false);
    }
  };

  const handlePost = async () => {
    if (postingInFlight.current) return;
    const userId = localStorage.getItem("username");
    if (!userId) {
      setPostMessage({ type: 'danger', text: 'User missing.' });
      return;
    }

    postingInFlight.current = true;
    setIsPosting(true);

    const changelogLink = `${window.location.origin}/changelog/${segmentId}`;
    let comment = `Change log has been added.\nLink: [View Change Log](${changelogLink})`;

    if (receiverNote.trim()) {
      comment += `\n\nMessage to recipients:\n${receiverNote}`;
    }

    try {
      const form = new FormData();
      form.append("segment", segmentId);
      form.append("userId", userId);
      form.append("comment", comment);
      attachments.forEach(file => form.append("attachments", file));

      await api.post('/discussion/create/', form, { headers: { "Content-Type": "multipart/form-data" } });

      // Send consolidated changelog email only if user toggled email ON
      if (triggerEmail) {
        try {
          const notifyForm = new FormData();
          notifyForm.append('segment_id', segmentId);
          notifyForm.append('receiver_note', receiverNote.trim());
          attachments.forEach(file => notifyForm.append('files', file));
          Object.values(changedPinnedFiles.current).forEach(f => { if (f) notifyForm.append('files', f, f.name); });
          await api.post('/logs/changelogs/notify/', notifyForm, {
            headers: { Authorization: `Token ${localStorage.getItem("Token")}` }
          });
        } catch { /* email failure is non-fatal */ }
      }

      changedPinnedFiles.current = { questionnaire: null, analysis_plan: null };
      setPostMessage({ type: 'success', text: triggerEmail ? 'Submitted & email sent' : 'Successfully submitted' });
      setAttachments([]); setReceiverNote(""); setFilesFrozen(false); setTriggerEmail(false);
    } catch {
      setPostMessage({ type: 'danger', text: 'Failed to post.' });
    } finally {
      postingInFlight.current = false;
      setIsPosting(false);
    }
  };

  if (!segmentId) {
    return (
      <Container className="mt-4">
        <Card><Card.Body>
          <Card.Title>No Project Selected</Card.Title>
          <Link to="/" className="btn btn-primary">Go to Project List</Link>
        </Card.Body></Card>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4 change-log-container">
      <Card className="custom-card">

        {/* Header UI */}
        <Card.Header className="custom-card-header" style={{ padding: 0, position: 'relative' }}>
          <button
            onClick={() => {
              const next = !headerCollapsed;
              setHeaderCollapsed(next);
              try { localStorage.setItem('cl_header_collapsed', next ? '1' : '0'); } catch {}
            }}
            style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 999, background: headerCollapsed ? '#22c55e' : '#ef4444', border: 'none', borderRadius: 20, color: '#fff', padding: '5px 18px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}
          >
            {headerCollapsed ? '▼ Show Panel' : '▲ Hide Panel'}
          </button>

          {/* ── Row 1: Title + links + action buttons ── */}
          <div style={{ padding: '8px 14px 7px', borderBottom: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            {/* Left: title + link chips */}
            <div>
              <div style={{ margin: '0 0 7px' }}>
                <h5 style={{ margin: 0, fontWeight: 700, fontSize: 16, letterSpacing: 0.2 }}>
                  Change Log — {segmentName}
                </h5>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {pinnedLinks.test.filter(l => l.url?.trim()).map((lnk, i) => {
                  const href = lnk.url.startsWith('http') ? lnk.url : `https://${lnk.url}`;
                  return (
                    <a key={i} href={href}
                      onClick={e => { e.preventDefault(); window.open(href, '_blank', 'noopener,noreferrer,width=1280,height=800'); }}
                      style={{ fontSize: 11, fontWeight: 600, color: '#fff', background: '#0369a1', borderRadius: 20, padding: '3px 11px', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      🔗 {lnk.label || `Test Link ${i + 1}`}
                    </a>
                  );
                })}
                {pinnedLinks.quota.filter(l => l.url?.trim()).map((lnk, i) => {
                  const href = lnk.url.startsWith('http') ? lnk.url : `https://${lnk.url}`;
                  return (
                    <a key={i} href={href}
                      onClick={e => { e.preventDefault(); window.open(href, '_blank', 'noopener,noreferrer,width=1280,height=800'); }}
                      style={{ fontSize: 11, fontWeight: 600, color: '#fff', background: '#5d3a8e', borderRadius: 20, padding: '3px 11px', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      📊 {lnk.label || `Quota Link ${i + 1}`}
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Right: action buttons */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <Tooltip title="Extract feedbacks from discussion comments using AI">
                <Button variant="warning" size="sm" onClick={handleFetchFeedbacks} disabled={fetchLoading}
                  style={{ color: '#fff', fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', padding: '6px 14px', borderRadius: 20 }}>
                  {fetchLoading ? <Spinner as="span" animation="border" size="sm" /> : <AutoFixHighIcon style={{ fontSize: 14 }} />}
                  Fetch Comments
                </Button>
              </Tooltip>
              {changeLogs.some(l => l.type_of_change === 'OTHER') && (
                <Tooltip title="AI will suggest better classifications for entries marked as Other">
                  <Button variant="outline-light" size="sm" onClick={handleClassifyOther} disabled={classifyLoading}
                    style={{ fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', padding: '6px 14px', borderRadius: 20 }}>
                    {classifyLoading ? <Spinner as="span" animation="border" size="sm" /> : <AutoFixHighIcon style={{ fontSize: 14 }} />}
                    Classify Other ({changeLogs.filter(l => l.type_of_change === 'OTHER').length})
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="AI categorizes each entry as New Addition, Change, or Issue">
                <Button variant="outline-light" size="sm" onClick={handleAiCategorize} disabled={aiCategorizing}
                  style={{ fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', padding: '6px 14px', borderRadius: 20 }}>
                  {aiCategorizing ? <Spinner as="span" animation="border" size="sm" /> : <AutoFixHighIcon style={{ fontSize: 14 }} />}
                  AI Categorize
                </Button>
              </Tooltip>
              {/* QNR Changes button hidden — feature in progress */}
            </div>
          </div>

          {/* ── Row 2: Attach + Message + Submit ── */}
          {!headerCollapsed && <div style={{ padding: '6px 14px 6px', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
            <Row className="g-2 align-items-end">
              <Col md={3}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3 }}>
                  Attachments
                </div>
                <Form.Control type="file" multiple disabled={filesFrozen}
                  onChange={e => { setAttachments(Array.from(e.target.files)); setFilesFrozen(true); }}
                  style={{ fontSize: 12 }}
                />
                {attachments.length > 0 && (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
                    {attachments.length} file(s) &nbsp;
                    <span style={{ cursor: 'pointer', color: '#93c5fd', textDecoration: 'underline' }}
                      onClick={() => { setAttachments([]); setFilesFrozen(false); }}>Clear</span>
                  </div>
                )}
              </Col>

              <Col md={7}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 5 }}>
                  Message to Recipients
                </div>
                <Form.Control as="textarea" rows={1} placeholder="Optional note to recipients..."
                  value={receiverNote} onChange={e => setReceiverNote(e.target.value)}
                  style={{ fontSize: 12, resize: 'none' }}
                />
              </Col>

              <Col md={2} className="d-flex flex-column align-items-end gap-2">
                <Tooltip title={triggerEmail ? "Email ON — click to disable" : "Enable email on submit"}>
                  <IconButton onClick={() => setTriggerEmail(p => !p)} size="small"
                    sx={{ color: triggerEmail ? '#60a5fa' : 'rgba(255,255,255,0.45)', bgcolor: triggerEmail ? 'rgba(96,165,250,0.18)' : 'transparent', border: '1px solid', borderColor: triggerEmail ? '#60a5fa' : 'rgba(255,255,255,0.25)', borderRadius: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                    <EmailIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button variant="info" style={{ fontWeight: 700, padding: '7px 22px', whiteSpace: 'nowrap', width: '100%' }} onClick={handlePost} disabled={isPosting}>
                  {isPosting ? <Spinner as="span" animation="border" size="sm" /> : 'Submit'}
                </Button>
              </Col>
            </Row>
          </div>}

          {/* ── Row 3: QNR + Analysis Plan ── */}
          {!headerCollapsed && <div style={{ padding: '5px 14px 7px', display: 'flex', gap: 8 }}>
            {[
              { key: 'questionnaire', label: 'QNR', icon: '📋', color: '#c084fc', accent: '#7c3aed', bg: 'rgba(124,58,237,0.15)', border: 'rgba(167,139,250,0.35)' },
              { key: 'analysis_plan', label: 'Analysis Plan', icon: '📊', color: '#fb923c', accent: '#ea580c', bg: 'rgba(234,88,12,0.15)', border: 'rgba(251,146,60,0.35)' },
            ].map(({ key, label, icon, color, accent, bg, border }) => {
              const att = pinnedAttachments[key];
              const isUploading = attachUploading[key];
              return (
                <div key={key} style={{ flex: 1, background: bg, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: '8px 12px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: att ? 4 : 0 }}>
                    <span style={{ fontWeight: 700, color, fontSize: 12 }}>
                      {icon} {label}{att ? ` (v${att.version})` : ''}
                    </span>
                    <input type="file" ref={el => { attachFileRefs.current[key] = el; }} style={{ display: 'none' }}
                      onChange={e => { if (e.target.files?.[0]) handleAttachmentUpload(key, e.target.files[0]); e.target.value = ''; }} />
                    <Button size="sm" variant="link" style={{ color, padding: '0 4px', fontSize: 11, textDecoration: 'none' }}
                      onClick={() => attachFileRefs.current[key]?.click()} disabled={isUploading}>
                      {att ? '↑ Replace' : '↑ Upload'}
                    </Button>
                  </div>
                  {isUploading && <div style={{ fontSize: 11, color, marginTop: 2 }}>Uploading…</div>}
                  {att && !isUploading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.original_filename}</div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                          v{att.version} · {att.uploaded_by} · {new Date(att.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      </div>
                      <Button size="sm" variant="link" onClick={() => handleAttachmentDownload(att.file_url, att.original_filename)} style={{ color, padding: '0 3px', fontSize: 11, textDecoration: 'none' }}>⬇</Button>
                      <Button size="sm" variant="link" style={{ color: '#f87171', padding: '0 3px', fontSize: 11, textDecoration: 'none' }} onClick={() => handleAttachmentDelete(key)}>✕</Button>
                    </div>
                  )}
                  {!att && !isUploading && (
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic', cursor: 'pointer', marginTop: 2 }}
                      onClick={() => attachFileRefs.current[key]?.click()}>
                      + Click to upload {label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>}

        </Card.Header>

        {attachments.length > 0 && (
          <Alert variant="warning" className="m-3">
            <strong>Note:</strong> Files (except QNR and Analysis Plan) are <u>not stored</u> for future use. They are attached to the email sent to project members.
          </Alert>
        )}

        <Card.Body className="p-0">
          {postMessage.text && (
            <div className="m-3" style={{
              background: postMessage.type === 'success' ? '#1a7a4a' : '#b91c1c',
              color: '#fff',
              borderRadius: 8,
              padding: '14px 20px',
              fontWeight: 700,
              fontSize: 15,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            }}>
              <span>{postMessage.type === 'success' ? '✅ ' : '❌ '}{postMessage.text}</span>
              <span style={{ cursor: 'pointer', fontSize: 18, lineHeight: 1 }} onClick={() => setPostMessage({ type: '', text: '' })}>✕</span>
            </div>
          )}

          {isLoading ? (
            <div className="text-center p-4"><Spinner animation="border" /><p className="mt-2">Loading change logs...</p></div>
          ) : (
            <>
              {error && <Alert variant="danger" className="m-3" dismissible onClose={() => setError(null)}>{error}</Alert>}

              {/* Filter bar */}
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <Form.Control
                  size="sm"
                  placeholder="Search question / details…"
                  value={filters.search}
                  onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                  style={{ maxWidth: 220 }}
                />
                <Form.Select
                  size="sm"
                  value={filters.status}
                  onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                  style={{ maxWidth: 160 }}
                >
                  <option value="">All Statuses</option>
                  {FINAL_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
                <Form.Select
                  size="sm"
                  value={filters.change_request}
                  onChange={e => setFilters(f => ({ ...f, change_request: e.target.value }))}
                  style={{ maxWidth: 160 }}
                >
                  <option value="">All Request Types</option>
                  {CHANGE_REQUEST_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
                <Form.Select
                  size="sm"
                  value={filters.type_of_change}
                  onChange={e => setFilters(f => ({ ...f, type_of_change: e.target.value }))}
                  style={{ maxWidth: 200 }}
                >
                  <option value="">All Types</option>
                  {TYPE_OF_CHANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
                <Form.Select
                  size="sm"
                  value={filters.requested_by}
                  onChange={e => setFilters(f => ({ ...f, requested_by: e.target.value }))}
                  style={{ maxWidth: 160 }}
                >
                  <option value="">All Requesters</option>
                  {uniqueRequesters.map(u => <option key={u} value={u}>{u}</option>)}
                </Form.Select>
                {activeFilterCount > 0 && (
                  <Button size="sm" variant="outline-secondary" onClick={() => setFilters({ status: '', change_request: '', type_of_change: '', requested_by: '', search: '' })}>
                    Clear ({activeFilterCount})
                  </Button>
                )}
                <span className="text-muted ms-auto" style={{ fontSize: 12 }}>
                  {filteredLogs.length} / {processedLogs.length} rows
                </span>
              </div>

              {/* Type-count chips */}
              {Object.keys(typeCounts).length > 0 && (
                <div style={{ padding: '6px 12px', background: '#f1f5f9', borderBottom: '1px solid #e5e7eb', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginRight: 2 }}>Type:</span>
                  {TYPE_OF_CHANGE_OPTIONS.filter(o => typeCounts[o.value]).map(o => {
                    const active = filters.type_of_change === o.value;
                    const aiHighlight = !active && aiClassifiedTypes.has(o.value);
                    return (
                      <span
                        key={o.value}
                        onClick={() => { setFilters(f => ({ ...f, type_of_change: active ? '' : o.value })); setAiClassifiedTypes(new Set()); }}
                        title={aiHighlight ? 'AI classified entries added to this type' : undefined}
                        style={{
                          cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 9px', borderRadius: 12, fontSize: 11, fontWeight: active || aiHighlight ? 700 : 400,
                          background: active ? '#1a3a5c' : aiHighlight ? '#fefce8' : '#fff',
                          color: active ? '#fff' : aiHighlight ? '#92400e' : '#374151',
                          border: `1.5px solid ${active ? '#1a3a5c' : aiHighlight ? '#f59e0b' : '#d1d5db'}`,
                          boxShadow: aiHighlight ? '0 0 0 2px rgba(245,158,11,0.18)' : 'none',
                          userSelect: 'none',
                          transition: 'all 0.2s',
                        }}
                      >
                        {aiHighlight && <span style={{ fontSize: 9, marginRight: 1 }}>✦</span>}
                        {o.label}
                        <span style={{
                          background: active ? 'rgba(255,255,255,0.25)' : aiHighlight ? '#fde68a' : '#e5e7eb',
                          color: active ? '#fff' : aiHighlight ? '#78350f' : '#374151',
                          borderRadius: 10, padding: '0 5px', fontSize: 10, fontWeight: 700,
                        }}>{typeCounts[o.value]}</span>
                      </span>
                    );
                  })}
                </div>
              )}

              <div className="table-responsive">
                <Table hover size="sm" className="change-log-table mb-0">
                  <thead>
                    <tr>
                      <th>#</th><th>Date *</th><th>Requested By</th><th>Change Request</th><th>Question *</th>
                      <th>Type</th><th>Category</th><th>Details *</th><th>CS Feedback</th><th>Script Feedback</th><th>Status</th>
                      <th style={{ minWidth: 48, textAlign: 'center' }}>{isDP ? <span title="Researcher visibility">👁</span> : ''}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredLogs.length === 0 ? (
                      <tr><td colSpan="12" className="text-center p-3">{processedLogs.length === 0 ? 'No records' : 'No records match the current filters'}</td></tr>
                    ) : filteredLogs.map((log) => {
                      const isImplemented = log.final_status === 'IMPLEMENTED';
                      const isRepeated = log.final_status === 'REPEATED_ISSUE';
                      const isOnHold = log.final_status === 'ON_HOLD';
                      const isRejected = log.final_status === 'REJECTED';
                      const isDiscussion = log.source === 'discussion';
                      const rowClass = [
                        isImplemented ? 'row-implemented' : '',
                        isRepeated ? 'row-repeated' : '',
                        isOnHold ? 'row-on-hold' : '',
                        isRejected ? 'row-rejected' : '',
                        isDiscussion ? 'row-discussion' : '',
                      ].filter(Boolean).join(' ');
                      return (
                      <tr key={log.id} className={rowClass}
                        onClick={() => { if (editRowId !== log.id && !isSaving) handleEditClick(log); }}
                        style={{ cursor: editRowId === log.id ? 'default' : 'pointer' }}
                      >
                        <td>
                          {log.displayIndex}
                          {isDiscussion && (
                            <span title="AI-extracted from discussion" style={{ marginLeft: 4, fontSize: 10, background: '#f59e0b', color: '#fff', borderRadius: 3, padding: '1px 4px', verticalAlign: 'middle' }}>AI</span>
                          )}
                        </td>
                        <td>{editRowId === log.id ?
                          <Form.Control type="date" name="date_of_request" value={editData.date_of_request} onChange={handleEditChange} className="form-control-sm" />
                          : log.date_of_request}
                        </td>
                        <td>{log.created_by?.username || "N/A"}</td>
                        <td>{editRowId === log.id ?
                          <Form.Select name="change_request" value={editData.change_request} onChange={handleEditChange} className="form-control-sm">
                            {CHANGE_REQUEST_TYPES.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                          </Form.Select>
                          : CHANGE_REQUEST_TYPES.find((c) => c.value === log.change_request)?.label}
                        </td>
                        <td>{editRowId === log.id ?
                          <Form.Control type="text" name="question_number" value={editData.question_number} onChange={handleEditChange} className="form-control-sm" />
                          : log.question_number}
                        </td>
                        <td>{editRowId === log.id ?
                          <Form.Select name="type_of_change" value={editData.type_of_change} onChange={handleEditChange} className="form-control-sm">
                            {TYPE_OF_CHANGE_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                          </Form.Select>
                          : TYPE_OF_CHANGE_OPTIONS.find((t) => t.value === log.type_of_change)?.label}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          {editRowId === log.id || categoryEditId === log.id ? (
                            <Form.Select
                              name="ai_category"
                              value={editRowId === log.id ? (editData.ai_category || '') : (log.ai_category || '')}
                              onChange={e => {
                                if (editRowId === log.id) handleEditChange(e);
                                else handleInlineCategoryChange(log.id, e.target.value);
                              }}
                              onBlur={() => { if (categoryEditId === log.id) setCategoryEditId(null); }}
                              autoFocus={categoryEditId === log.id}
                              className="form-control-sm"
                              style={{ minWidth: 110 }}
                            >
                              <option value="">— None —</option>
                              <option value="NEW_ADDITION">New Addition</option>
                              <option value="CHANGE">Change</option>
                              <option value="ISSUE">Issue</option>
                            </Form.Select>
                          ) : (() => {
                            const cfg = {
                              NEW_ADDITION: { label: 'New Addition', bg: '#0369a1' },
                              CHANGE:       { label: 'Change',       bg: '#d97706' },
                              ISSUE:        { label: 'Issue',        bg: '#dc2626' },
                            }[log.ai_category];
                            return cfg ? (
                              <span
                                onClick={() => setCategoryEditId(log.id)}
                                title="Click to change"
                                style={{ background: cfg.bg, color: '#fff', borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}
                              >
                                {cfg.label}
                              </span>
                            ) : (
                              <span
                                onClick={() => setCategoryEditId(log.id)}
                                title="Click to set category"
                                style={{ color: '#9ca3af', fontSize: 10, cursor: 'pointer' }}
                              >+ set</span>
                            );
                          })()}
                        </td>
                        <td>{editRowId === log.id ?
                          <Form.Control as="textarea" rows={2} name="change_details" value={editData.change_details} onChange={handleEditChange} className="form-control-sm" />
                          : <div className="table-cell-text">{highlightQuoted(log.change_details)}</div>}
                        </td>
                        <td>{editRowId === log.id ?
                          <Form.Control as="textarea" rows={2} name="cs_feedback" value={editData.cs_feedback} onChange={handleEditChange} className="form-control-sm" />
                          : <div className="table-cell-text">{highlightQuoted(log.cs_feedback)}</div>}
                        </td>
                        <td>{editRowId === log.id ?
                          <Form.Control as="textarea" rows={2} name="scripting_resource_feedback" value={editData.scripting_resource_feedback} onChange={handleEditChange} className="form-control-sm" />
                          : <div className="table-cell-text">{highlightQuoted(log.scripting_resource_feedback)}</div>}
                        </td>
                        <td>{editRowId === log.id ?
                          <Form.Select name="final_status" value={editData.final_status} onChange={handleEditChange} className="form-control-sm">
                            {FINAL_STATUS_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                          </Form.Select>
                          : FINAL_STATUS_OPTIONS.find((s) => s.value === log.final_status)?.label}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {isDP && (
                              <Tooltip title={log.visible_to_researcher ? 'Visible to researchers — click to hide' : 'Hidden from researchers — click to show'}>
                                <span
                                  onClick={() => { if (togglingId !== log.id) handleToggleResearcher(log.id, log.visible_to_researcher); }}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                    cursor: togglingId === log.id ? 'default' : 'pointer',
                                    padding: '2px 6px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                                    background: log.visible_to_researcher ? '#e0f2fe' : '#f1f5f9',
                                    color: log.visible_to_researcher ? '#0369a1' : '#64748b',
                                    border: `1px solid ${log.visible_to_researcher ? '#bae6fd' : '#cbd5e1'}`,
                                    opacity: togglingId === log.id ? 0.5 : 1,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {log.visible_to_researcher
                                    ? <><VisibilityIcon sx={{ fontSize: 12 }} /> Shown</>
                                    : <><VisibilityOffIcon sx={{ fontSize: 12 }} /> Hidden</>}
                                </span>
                              </Tooltip>
                            )}
                            {editRowId === log.id && (
                              <div className="actions-column">
                                <Button variant="success" size="sm" onClick={() => handleSaveEdit(log.id)} disabled={isSaving} title="Save">
                                  {isSaving ? <Spinner as="span" animation="border" size="sm" /> : '✓'}
                                </Button>
                                <Button variant="secondary" size="sm" onClick={handleCancelEdit} title="Cancel">✕</Button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>

                  <tfoot>
                    {newEntryError && (
                      <tr>
                        <td colSpan="12" style={{ padding: '6px 12px', background: '#fef2f2', color: '#b91c1c', fontSize: 12, fontWeight: 600, borderTop: '1px solid #fca5a5' }}>
                          ⚠ {newEntryError}
                          <span style={{ float: 'right', cursor: 'pointer', fontWeight: 400 }} onClick={() => setNewEntryError('')}>✕</span>
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td>+</td>
                      <td><Form.Control type="date" name="date_of_request" value={newEntry.date_of_request} onChange={handleNewChange} className="form-control-sm" /></td>
                      <td>{localStorage.getItem("username") || "You"}</td>
                      <td><Form.Select name="change_request" value={newEntry.change_request} onChange={handleNewChange} className="form-control-sm">
                        {CHANGE_REQUEST_TYPES.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                      </Form.Select></td>
                      <td><Form.Control type="text" name="question_number" value={newEntry.question_number} onChange={handleNewChange} className="form-control-sm" /></td>
                      <td><Form.Select name="type_of_change" value={newEntry.type_of_change} onChange={handleNewChange} className="form-control-sm">
                        {TYPE_OF_CHANGE_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                      </Form.Select></td>
                      <td><Form.Select name="ai_category" value={newEntry.ai_category || ''} onChange={handleNewChange} className="form-control-sm">
                        <option value="">— None —</option>
                        <option value="NEW_ADDITION">New Addition</option>
                        <option value="CHANGE">Change</option>
                        <option value="ISSUE">Issue</option>
                      </Form.Select></td>
                      <td><Form.Control as="textarea" rows={2} name="change_details" value={newEntry.change_details} onChange={handleNewChange} className="form-control-sm" /></td>
                      <td><Form.Control as="textarea" rows={2} name="cs_feedback" value={newEntry.cs_feedback} onChange={handleNewChange} className="form-control-sm" /></td>
                      <td><Form.Control as="textarea" rows={2} name="scripting_resource_feedback" value={newEntry.scripting_resource_feedback} onChange={handleNewChange} className="form-control-sm" /></td>
                      <td><Form.Select name="final_status" value={newEntry.final_status} onChange={handleNewChange} className="form-control-sm">
                        {FINAL_STATUS_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                      </Form.Select></td>
                      <td>
                        <Button variant="primary" size="sm" onClick={handleSaveNew} disabled={isSaving} className="w-100">
                          {isSaving ? <Spinner as="span" animation="border" size="sm" /> : "+ Add"}
                        </Button>
                      </td>
                    </tr>
                  </tfoot>

                </Table>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* QNR Change Reference Panel */}
      <Modal show={showQnrPanel} onHide={() => { setShowQnrPanel(false); setQnrError(''); }} size="xl" scrollable>
        <Modal.Header closeButton style={{ background: '#1e293b', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            📋 QNR Change Reference
            <span style={{ fontSize: 12, fontWeight: 400, color: 'rgba(255,255,255,0.5)' }}>
              {processedLogs.length} entries · {changesByQuestion.length} questions
            </span>
          </Modal.Title>
          {pinnedAttachments.questionnaire && (
            <Button size="sm" variant="outline-light" className="me-2"
              onClick={() => handleAttachmentDownload(pinnedAttachments.questionnaire.file_url, pinnedAttachments.questionnaire.original_filename)}
              style={{ fontSize: 12, borderRadius: 20, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              ⬇ QNR v{pinnedAttachments.questionnaire.version}
            </Button>
          )}
          <Button size="sm"
            variant={qnrInstructions ? 'success' : 'outline-warning'}
            className="me-3"
            onClick={handleGenerateQnrInstructions}
            disabled={qnrGenerating}
            style={{ fontSize: 12, borderRadius: 20, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            {qnrGenerating ? '⚙️ Analyzing…' : qnrInstructions ? '✓ AI Ready — Re-analyze' : '🤖 Analyze with AI'}
          </Button>
        </Modal.Header>

        <Modal.Body style={{ background: '#f8fafc', padding: '20px 24px' }}>

          {/* AI Instructions Section */}
          {qnrError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#b91c1c', fontSize: 13 }}>
              ⚠️ {qnrError}
            </div>
          )}
          {qnrGenerating && (
            <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 10, padding: '28px 24px', marginBottom: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1d4ed8', marginBottom: 8 }}>⚙️ Analyzing QNR against changelog…</div>
              <div style={{ fontSize: 12, color: '#3b82f6' }}>Gemini is reading the questionnaire and generating per-question editing instructions</div>
            </div>
          )}
          {qnrInstructions && (
            <div style={{ marginBottom: 24 }}>
              {/* Overall summary bar */}
              <div style={{ background: '#1e293b', borderRadius: 10, padding: '12px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: '#fff', fontWeight: 700 }}>🤖 AI Editing Instructions</span>
                <span style={{ fontSize: 12, color: '#94a3b8', flex: 1 }}>{qnrInstructions.overall_summary}</span>
                <Button size="sm" variant="outline-light" onClick={() => setQnrInstructions(null)}
                  style={{ fontSize: 11, borderRadius: 16, padding: '2px 10px' }}>Clear</Button>
              </div>

              {/* Per-question AI cards */}
              {(qnrInstructions.questions || []).map((q, i) => {
                const p = q.priority || 'LOW';
                const cfg = {
                  HIGH:   { bg: '#fff5f5', border: '#fca5a5', badgeColor: '#b91c1c', badgeBg: '#fee2e2', numBg: '#b91c1c' },
                  MEDIUM: { bg: '#fefce8', border: '#fde047', badgeColor: '#92400e', badgeBg: '#fef3c7', numBg: '#b45309' },
                  LOW:    { bg: '#f0fdf4', border: '#86efac', badgeColor: '#166534', badgeBg: '#dcfce7', numBg: '#16a34a' },
                }[p] || { bg: '#f8fafc', border: '#e2e8f0', badgeColor: '#475569', badgeBg: '#f1f5f9', numBg: '#64748b' };

                return (
                  <div key={i} style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
                    <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${cfg.border}` }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: cfg.badgeColor }}>
                        {q.question_number === '—' ? 'General' : `Q: ${q.question_number}`}
                      </span>
                      <span style={{ background: cfg.badgeBg, color: cfg.badgeColor, border: `1px solid ${cfg.border}`, borderRadius: 99, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>
                        {p}
                      </span>
                      <span style={{ fontSize: 12, color: cfg.badgeColor, flex: 1, opacity: 0.85 }}>{q.summary}</span>
                    </div>
                    <div style={{ padding: '12px 16px' }}>
                      {q.qnr_current && (
                        <div style={{ background: 'rgba(255,255,255,0.75)', borderRadius: 6, padding: '7px 12px', marginBottom: 10, fontSize: 12, color: '#475569', borderLeft: '3px solid #94a3b8' }}>
                          <span style={{ fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 3, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current in QNR</span>
                          {q.qnr_current}
                        </div>
                      )}
                      {(q.instructions || []).map((step, j) => (
                        <div key={j} style={{ display: 'flex', gap: 10, marginBottom: 7, alignItems: 'flex-start' }}>
                          <span style={{ background: cfg.numBg, color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>
                            {j + 1}
                          </span>
                          <span style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.55 }}>{step.replace(/^Step \d+:\s*/i, '')}</span>
                        </div>
                      ))}
                      {q.conflicts && (
                        <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 6, padding: '7px 12px', marginTop: 8, fontSize: 12, color: '#c2410c' }}>
                          ⚠️ {q.conflicts}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              <div style={{ borderTop: '2px dashed #e2e8f0', margin: '20px 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Changelog Reference</span>
              </div>
            </div>
          )}

          {/* Legend */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginRight: 4 }}>Legend:</span>
            {[
              { label: 'New Addition', bg: '#dcfce7', color: '#15803d', border: '#86efac' },
              { label: 'Change',       bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
              { label: 'Issue',        bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' },
              { label: 'Unset',        bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
            ].map(c => (
              <span key={c.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 99, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
                {c.label}
              </span>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8' }}>Open QNR in Excel alongside this window</span>
          </div>

          {changesByQuestion.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No changelog entries yet.</div>
          ) : changesByQuestion.map(([qNum, entries]) => {
            const hasIssue = entries.some(e => e.ai_category === 'ISSUE');
            const hasChange = entries.some(e => e.ai_category === 'CHANGE');
            const headerBg = hasIssue ? '#fee2e2' : hasChange ? '#fef9c3' : '#f0fdf4';
            const headerBorder = hasIssue ? '#fca5a5' : hasChange ? '#fde047' : '#86efac';
            const headerColor = hasIssue ? '#991b1b' : hasChange ? '#78350f' : '#166534';

            return (
              <div key={qNum} style={{ marginBottom: 16, background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                {/* Question header */}
                <div style={{ background: headerBg, borderBottom: `1px solid ${headerBorder}`, padding: '9px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: headerColor }}>
                    {qNum === '—' ? 'General / No Question' : `Q: ${qNum}`}
                  </span>
                  <span style={{ fontSize: 11, color: headerColor, opacity: 0.7 }}>{entries.length} change{entries.length !== 1 ? 's' : ''}</span>
                </div>

                {/* Entries */}
                {entries.map((log, i) => {
                  const catCfg = {
                    NEW_ADDITION: { label: 'New Addition', bg: '#dcfce7', color: '#15803d', border: '#86efac' },
                    CHANGE:       { label: 'Change',       bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
                    ISSUE:        { label: 'Issue',        bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' },
                  }[log.ai_category] || { label: 'Unset', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };

                  const statusLabel = FINAL_STATUS_OPTIONS.find(s => s.value === log.final_status)?.label || log.final_status;
                  const typeLabel = TYPE_OF_CHANGE_OPTIONS.find(t => t.value === log.type_of_change)?.label || log.type_of_change;

                  return (
                    <div key={log.id} style={{ padding: '12px 16px', borderBottom: i < entries.length - 1 ? '1px solid #f1f5f9' : 'none', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      {/* Category badge */}
                      <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', background: catCfg.bg, color: catCfg.color, border: `1px solid ${catCfg.border}`, borderRadius: 6, padding: '2px 9px', fontSize: 10, fontWeight: 700, marginTop: 2 }}>
                        {catCfg.label}
                      </span>

                      {/* Main content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, color: '#334155' }}>{typeLabel}</span>
                          <span style={{ color: '#94a3b8' }}>·</span>
                          <span>{log.date_of_request}</span>
                          <span style={{ color: '#94a3b8' }}>·</span>
                          <span>{log.created_by?.username || 'N/A'}</span>
                          {log.change_request && (
                            <>
                              <span style={{ color: '#94a3b8' }}>·</span>
                              <span style={{ background: '#f1f5f9', borderRadius: 4, padding: '1px 6px', fontSize: 11 }}>
                                {CHANGE_REQUEST_TYPES.find(c => c.value === log.change_request)?.label}
                              </span>
                            </>
                          )}
                          <span style={{ marginLeft: 'auto', background: '#f1f5f9', borderRadius: 4, padding: '1px 6px', fontSize: 11, color: '#475569', fontWeight: 500 }}>
                            {statusLabel}
                          </span>
                        </div>
                        <p style={{ margin: 0, fontSize: 13, color: '#1e293b', lineHeight: 1.55 }}>{log.change_details}</p>
                        {log.cs_feedback && (
                          <p style={{ margin: '6px 0 0', fontSize: 12, color: '#475569', fontStyle: 'italic', borderLeft: '2px solid #e2e8f0', paddingLeft: 8 }}>
                            CS: {log.cs_feedback}
                          </p>
                        )}
                        {log.scripting_resource_feedback && (
                          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#475569', fontStyle: 'italic', borderLeft: '2px solid #e2e8f0', paddingLeft: 8 }}>
                            Script: {log.scripting_resource_feedback}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </Modal.Body>

        <Modal.Footer style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 8 }}>
          <span className="me-auto" style={{ fontSize: 12, color: '#94a3b8' }}>
            {processedLogs.filter(l => l.ai_category === 'ISSUE').length} issues · {processedLogs.filter(l => l.ai_category === 'CHANGE').length} changes · {processedLogs.filter(l => l.ai_category === 'NEW_ADDITION').length} new additions
          </span>
          <Button
            variant="success"
            onClick={handleUpdateQnr}
            disabled={qnrUpdating || !pinnedAttachments.questionnaire}
            style={{ fontSize: 13, borderRadius: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            {qnrUpdating ? '⚙️ Generating…' : '📥 Download Updated QNR'}
          </Button>
          <Button variant="secondary" onClick={() => setShowQnrPanel(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* AI Feedback Extraction Preview Dialog */}
      <Modal show={fetchDialog} onHide={() => { if (!fetchLoading) { setFetchDialog(false); setFetchedItems([]); setFetchError(''); } }} size="xl" scrollable>
        <Modal.Header closeButton style={{ background: '#fffbeb', borderBottom: '2px solid #f59e0b' }}>
          <Modal.Title style={{ fontSize: 16, color: '#92400e' }}>
            <AutoFixHighIcon fontSize="small" style={{ marginRight: 6, verticalAlign: 'middle' }} />
            AI-Extracted Feedbacks from Discussion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fetchLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" variant="warning" />
              <p className="mt-2 text-muted">Analysing discussion comments with AI…</p>
            </div>
          )}
          {fetchError && !fetchLoading && (
            <Alert variant="danger">{fetchError}</Alert>
          )}
          {!fetchLoading && !fetchError && fetchedItems.length === 0 && (
            <Alert variant="info">No actionable feedbacks found in the discussion comments.</Alert>
          )}
          {!fetchLoading && fetchedItems.length > 0 && (
            <>
              <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                Review and edit each item before saving. Uncheck items you don't want to add.
              </p>
              {fetchedItems.map((item) => (
                <div key={item._id} style={{
                  border: `1px solid ${item._selected ? '#f59e0b' : '#e5e7eb'}`,
                  borderLeft: `4px solid ${item._selected ? '#f59e0b' : '#d1d5db'}`,
                  borderRadius: 6,
                  padding: '10px 14px',
                  marginBottom: 10,
                  background: item._selected ? '#fffbeb' : '#f9fafb',
                  opacity: item._selected ? 1 : 0.6,
                }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Form.Check
                      type="checkbox"
                      checked={item._selected}
                      onChange={e => updateFetchedItem(item._id, '_selected', e.target.checked)}
                      label={<strong style={{ fontSize: 13 }}>Include this entry</strong>}
                    />
                  </div>
                  <Row className="g-2">
                    <Col xs={6} md={2}>
                      <Form.Label style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Question #</Form.Label>
                      <Form.Control size="sm" value={item.question_number || ''} onChange={e => updateFetchedItem(item._id, 'question_number', e.target.value)} disabled={!item._selected} />
                    </Col>
                    <Col xs={6} md={2}>
                      <Form.Label style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Date</Form.Label>
                      <Form.Control type="date" size="sm" value={item.date_of_request || ''} onChange={e => updateFetchedItem(item._id, 'date_of_request', e.target.value)} disabled={!item._selected} />
                    </Col>
                    <Col xs={12} md={3}>
                      <Form.Label style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Type of Change</Form.Label>
                      <Form.Select size="sm" value={item.type_of_change || 'OTHER'} onChange={e => updateFetchedItem(item._id, 'type_of_change', e.target.value)} disabled={!item._selected}>
                        {TYPE_OF_CHANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </Form.Select>
                    </Col>
                    <Col xs={12} md={2}>
                      <Form.Label style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Change Request</Form.Label>
                      <Form.Select size="sm" value={item.change_request || 'IN_TEST_LINK'} onChange={e => updateFetchedItem(item._id, 'change_request', e.target.value)} disabled={!item._selected}>
                        {CHANGE_REQUEST_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </Form.Select>
                    </Col>
                    <Col xs={12} md={3}>
                      <Form.Label style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Change Details</Form.Label>
                      <Form.Control as="textarea" rows={2} size="sm" value={item.change_details || ''} onChange={e => updateFetchedItem(item._id, 'change_details', e.target.value)} disabled={!item._selected} />
                    </Col>
                  </Row>
                </div>
              ))}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <span className="me-auto text-muted" style={{ fontSize: 12 }}>
            {fetchedItems.filter(i => i._selected).length} of {fetchedItems.length} items selected
          </span>
          <Button variant="secondary" onClick={() => { setFetchDialog(false); setFetchedItems([]); setFetchError(''); }} disabled={fetchLoading}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleSaveExtracted}
            disabled={fetchLoading || fetchedItems.filter(i => i._selected).length === 0}
            style={{ color: '#fff' }}
          >
            {fetchLoading ? <Spinner as="span" animation="border" size="sm" /> : `Save ${fetchedItems.filter(i => i._selected).length} to Changelog`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* AI Classify Other Modal */}
      <Modal show={classifyDialog} onHide={() => { if (!classifyLoading && !classifyApplying) { setClassifyDialog(false); setClassifyItems([]); setClassifyError(''); } }} size="xl" scrollable>
        <Modal.Header closeButton style={{ background: '#f0f4ff', borderBottom: '1px solid #dde5f5' }}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
            <AutoFixHighIcon fontSize="small" style={{ marginRight: 6, verticalAlign: 'middle', color: '#3b5bdb' }} />
            AI Classification Suggestions — "Other" Entries
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {classifyLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Analysing entries with AI…</p>
            </div>
          )}
          {classifyError && !classifyLoading && (
            <Alert variant="danger">{classifyError}</Alert>
          )}
          {!classifyLoading && !classifyError && classifyItems.length === 0 && (
            <Alert variant="info">No OTHER entries found for this segment.</Alert>
          )}
          {!classifyLoading && classifyItems.length > 0 && (
            <>
              <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                Review each suggestion. Uncheck any you want to keep as "Other". You can also override the suggested type.
              </p>
              <Table bordered hover size="sm" style={{ fontSize: 13 }}>
                <thead style={{ background: '#f0f4ff' }}>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th style={{ width: 90 }}>Question</th>
                    <th>Change Details</th>
                    <th style={{ width: 220 }}>Suggested Type</th>
                    <th>AI Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {classifyItems.map((item, idx) => (
                    <tr key={item.id} style={{ background: item._accepted ? '#f6fff6' : '#fff8f8' }}>
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={item._accepted}
                          onChange={e => setClassifyItems(prev => prev.map((it, i) => i === idx ? { ...it, _accepted: e.target.checked } : it))}
                        />
                      </td>
                      <td style={{ fontWeight: 600, color: '#1a3a5c' }}>{item.question_number}</td>
                      <td style={{ maxWidth: 300, wordBreak: 'break-word' }}>{item.change_details}</td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={item._type}
                          onChange={e => setClassifyItems(prev => prev.map((it, i) => i === idx ? { ...it, _type: e.target.value } : it))}
                          disabled={!item._accepted}
                        >
                          {TYPE_OF_CHANGE_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </Form.Select>
                        {item._type !== item.suggested_type && (
                          <small className="text-muted">AI suggested: {item.suggested_label}</small>
                        )}
                      </td>
                      <td style={{ color: '#555', fontStyle: 'italic', fontSize: 12 }}>{item.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <span className="me-auto text-muted" style={{ fontSize: 12 }}>
            {classifyItems.filter(i => i._accepted).length} of {classifyItems.length} entries selected
          </span>
          <Button variant="secondary" onClick={() => { setClassifyDialog(false); setClassifyItems([]); setClassifyError(''); }} disabled={classifyApplying}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleApplyClassifications}
            disabled={classifyApplying || classifyItems.filter(i => i._accepted && i._type !== 'OTHER').length === 0}
          >
            {classifyApplying ? <Spinner as="span" animation="border" size="sm" /> : `Apply ${classifyItems.filter(i => i._accepted && i._type !== 'OTHER').length} Classifications`}
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}

export default ChangeLogManager;
